import { formatDateTime, formatRunDuration, money } from '../format';
import type { RunSummary } from '../../shared/types';
import { TraceLoader } from './TraceTimeline';

/**
 * Full detail for one run — meta grid, error, and either the full trace
 * timeline (LLM agents with a stored JSONL) or the raw meta JSON
 * (deterministic agents such as Archivist and Publisher). Shared between the
 * Activity accordion (inline expansion) and the /activity/:id deep link.
 */
export function RunPanel({ run }: { run: RunSummary }) {
  const hasMeta = run.meta !== null && Object.keys(run.meta).length > 0;

  return (
    <div className="run-panel">
      <div className="meta-grid">
        <span className="label">Status</span>
        <span className={run.status === 'failed' ? 'fail' : undefined}>{run.status}</span>
        <span className="label">Trigger</span>
        <span className="muted">{run.trigger}</span>
        <span className="label">Started</span>
        <span className="mono">{formatDateTime(run.startedAt)}</span>
        <span className="label">Finished</span>
        <span className="mono">{run.finishedAt ? formatDateTime(run.finishedAt) : '—'}</span>
        <span className="label">Took</span>
        <span className="mono">{formatRunDuration(run)}</span>
        <span className="label">Tokens</span>
        <span className="mono">
          {run.tokensIn ?? 0} in / {run.tokensOut ?? 0} out
        </span>
        <span className="label">Local model estimate</span>
        <span className="mono">{money(run.costUsd)}</span>
        <span className="label">Trace</span>
        <span className="mono faint">{run.tracePath ?? '—'}</span>
        <span className="label">Summary</span>
        <span>{run.summary ?? '—'}</span>
      </div>

      {run.error ? (
        <>
          <div className="label" style={{ marginTop: 22, marginBottom: 8 }}>
            Error
          </div>
          <pre className="tl-pre fail">{run.error}</pre>
        </>
      ) : null}

      {run.hasTrace ? (
        <>
          <div className="label" style={{ marginTop: 24, marginBottom: 10 }}>
            Trace
          </div>
          <TraceLoader runId={run.id} />
        </>
      ) : null}

      {hasMeta ? (
        <>
          <div className="label" style={{ marginTop: 24, marginBottom: 10 }}>
            Meta
          </div>
          <pre className="tl-pre">{JSON.stringify(run.meta, null, 2)}</pre>
        </>
      ) : null}

      {!run.hasTrace && !hasMeta ? (
        <div className="empty" style={{ marginTop: 20 }}>
          Awaiting data — no trace or meta recorded for this run.
        </div>
      ) : null}
    </div>
  );
}
